import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/shared/stores/authStore';
import {
    TrendingUp,
    Users,
    DollarSign,
    Shield,
    CheckCircle,
    Target,
    Zap,
    Star,
    Wallet,
    LogOut
} from 'lucide-react';
import Header from '@/react-app/components/Header';
import SavingsPackageCard from '@/react-app/components/SavingsPackageCard';
import { api } from '@/shared/api/endpoints';
import type { CreateDepositInput, SavingsPackage, UserStats, IUserPackage } from '@/shared/types';
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useMutation } from "react-query";
import Swal from "sweetalert2";
import logo from "../../assets/trade-logo.jpeg";
import { useBlockPayment } from '@/react-app/hooks/useBlockPay';
import { useAppKit, useAppKitAccount, useDisconnect } from '@reown/appkit/react';
import mobileBanking from "../../assets/mobile-banking-secure.png";


export default function save() {
    const { user: authStoreUser } = useAuthStore();
    const [packages, setPackages] = useState<SavingsPackage[]>([]);
    const [stats, setStats] = useState<UserStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedPackage, setSelectedPackage] = useState<SavingsPackage | null>(null);
    const [depositAmount, setDepositAmount] = useState<number>(0);
    const [showDepositModal, setShowDepositModal] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState<'fiat' | 'crypto'>('fiat');
    const [paymentReference, setPaymentReference] = useState<string | null>(null);
    // const [cryptoStatus, setCryptoStatus] = useState<string | null>(null);
    const { open } = useAppKit();
    const { isConnected, address } = useAppKitAccount();
    const { disconnect } = useDisconnect();

    const web3Payment = useBlockPayment();
    const [amlAccepted, setAmlAccepted] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        fetchData();
    }, []);

    // const acceptCrypto = async () => {
    //     const url = "https://api.blockradar.co/v1/payment_links";
    //     const form = new FormData();

    //     const amount = depositAmount.toString();
    //     const reference = `${authStoreUser?._id}_ORDER_${Date.now()}`;
    //     const redirectUrl = "https://tradeenrich.com/save";
    //     form.append("amount", amount);
    //     form.append("description", `Trade Enrich Deposit for ${selectedPackage?.name}`);
    //     form.append("inactiveMessage", "");
    //     form.append(
    //         "metadata",
    //         JSON.stringify({
    //             user_id: authStoreUser?._id,
    //             package_id: selectedPackage?._id.toString(),
    //         })
    //     );
    //     form.append("name", `${authStoreUser?.firstName + " " + authStoreUser?.lastName} Deposit`);
    //     form.append("paymentLimit", amount);
    //     form.append("redirectUrl", redirectUrl);
    //     // form.append("slug", `payment-link`);
    //     form.append("successMessage", "Your payment was successful!");

    //     const options: RequestInit = {
    //         method: "POST",
    //         headers: {
    //             "x-api-key": import.meta.env.VITE_BLOCKRADAR_API_KEY as string,
    //         },
    //         body: form,
    //     };

    //     const response = await fetch(url, options);
    //     const data = await response.json();
    //     console.log(data);
    //     if (data?.data?.url) {
    //         const customerUrl = `${data.data.url}?name=${encodeURIComponent(
    //             authStoreUser?.firstName + " " + authStoreUser?.lastName || "Customer"
    //         )}&email=${encodeURIComponent(
    //             authStoreUser?.email || "customer@example.com"
    //         )}&reference=${reference}&amount=${amount}&redirectUrl=${encodeURIComponent(
    //             redirectUrl
    //         )}`;

    //         localStorage.setItem("payment_reference", reference);

    //         toast.success("Redirecting to payment gateway...");
    //         window.location.href = customerUrl;
    //     } else {
    //         throw new Error(data.message || "Failed to create payment link");
    //     }


    // };

    const handleCrypto = async () => {

        if (!selectedPackage || !authStoreUser) return;

        try {

            const result = await web3Payment({
                amount: Number(depositAmount),
                userId: authStoreUser._id,
                plan: selectedPackage.name,
                planId: selectedPackage._id
            });


            if (result === true) {
                const savedData = localStorage.getItem("save_data");
                let parsedInvestData: CreateDepositInput | null = null;

                if (!savedData) return;

                try {
                    parsedInvestData = JSON.parse(savedData) as CreateDepositInput;
                    if (!parsedInvestData) return;
                    toast.success("finalizing crypto saving...");
                    const reference = `${authStoreUser?._id}_CRYPTO_${Date.now()}`;
                    const response = await api.transaction.createDeposit({
                        amount: parsedInvestData.amount,
                        packageId: parsedInvestData.packageId,
                        payment_method: parsedInvestData.payment_method,
                        payment_reference: reference
                    });
                    if (response.success) {
                        toast.success("investment created successfully 🎉");
                        setShowDepositModal(false);
                        setSelectedPackage(null);
                        setDepositAmount(0);
                        setPaymentMethod('fiat');
                        localStorage.removeItem("payment_reference");
                        localStorage.removeItem("payment_method");
                        setPaymentReference(null);
                        localStorage.removeItem("save_data");
                        fetchData();
                        Swal.fire({
                            icon: "success",
                            title: "Congratulations 🎉",
                            text: "investment created successfully",
                        });


                    } else {
                        toast.error(response.error || 'Failed to create investment');
                        console.log(response.error)
                    }

                }
                catch (error: any) {
                    console.error("Failed to parse save_data from localStorage:", error);
                    toast.error("Failed to parse save_data from localStorage:", error);

                }
            }
            else if (typeof result === 'string') {
                Swal.fire({
                    icon: "error",
                    text: `Transaction failed: ${result}`,

                });
            }
        }
        catch (error) {
            Swal.fire({
                icon: "error",
                text: `Apologies! Transaction failed, please try again.${error}`,

            });
            console.error('Unhandled error in confirm:', error);

        };
    }

    const fetchData = async () => {
        try {
            const [packagesResponse, statsResponse] = await Promise.all([
                api.package.getAll(),
                api.user.getStats(),
            ]);

            setPackages(packagesResponse.data || []);

            if (statsResponse.success) {
                setStats(statsResponse.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePackageSelect = (pkg: SavingsPackage) => {
        setSelectedPackage(pkg);
        setDepositAmount(pkg.minDeposit);
        setShowDepositModal(true);
        setPaymentMethod('fiat');
    };


    const { mutate: handleAcceptPayment, isLoading: isAcceptingPayment } =
        useMutation({
            mutationFn: api.transaction.acceptPayment,
            onSuccess: (response) => {


                toast.success("Redirecting to payment gateway...");
                if (response.success && response.data) {
                    console.log("Payment accepted successfully:", response);

                    setPaymentReference(response.data.data.reference);
                    localStorage.setItem("payment_reference", response.data.data.reference);

                    window.location.href = response.data.data.authorization_url;
                }
            },
            onError: (error: any) => {
                console.error("Payment acceptance error:", error);
                toast.error(error.message || "Failed to initiate payment");
            },
        });

    // const { mutate: handleVerifyPayment, isLoading: isVerifyingPayment } = useMutation({
    //     mutationFn: async ({ reference }: { reference: string; }) => {
    //         return await api.transaction.verifyPayment(reference);
    //     },
    //     onSuccess: async (verificationResponse) => {
    //         if (verificationResponse.success) {
    //             toast.success("Payment verified! Finalizing investment...");

    //             if (!selectedPackage?._id) return;
    //             const response = await api.transaction.createDeposit({
    //                 amount: depositAmount,
    //                 packageId: selectedPackage?._id.toString(),
    //                 payment_method: paymentMethod,
    //                 payment_reference: paymentReference ?? ''
    //             });

    //             if (response.success) {
    //                 toast.success("investment created successfully 🎉");
    //                 setShowDepositModal(false);
    //                 setSelectedPackage(null);
    //                 setDepositAmount(0);
    //                 setPaymentMethod('fiat');
    //                 fetchData(); // Refresh data
    //             } else {
    //                 toast.error(response.error || 'Failed to create savings');
    //                 console.log(response.error)
    //             }

    //             localStorage.removeItem("payment_reference");
    //             setPaymentReference(null);
    //         } else {
    //             toast.error("Payment verification failed. Please contact support.");

    //         }
    //     },
    //     onError: (error: any) => {
    //         console.error("Payment verification error:", error);
    //         toast.error(error.message || "Payment verification failed");
    //     },
    // });

    const handleDeposit = async () => {
        if (!selectedPackage || !depositAmount || !authStoreUser) return;
        if (!authStoreUser.isVerified) {
            Swal.fire({
                icon: "error",
                title: "KYC Needed",
                text: "You need to complete KYC to save",
            });
            return;
        }
        setPaymentReference(null);
        try {
            const paymentPayload = {
                email: authStoreUser.email,
                amount: Number(depositAmount * 100 * 1500),
                packageId: selectedPackage._id.toString()

            };

            const createDeposit: CreateDepositInput = {
                amount: Number(depositAmount),
                packageId: selectedPackage?._id,
                payment_method: paymentMethod,
                payment_reference: paymentReference ?? ''
            }

            localStorage.setItem("save_data", JSON.stringify(createDeposit));
            localStorage.setItem('payment_method', paymentMethod);

            if (paymentMethod == "fiat") {
                await handleAcceptPayment(paymentPayload);
            } else {
                await handleCrypto();
            }


        } catch (error) {
            console.error('Error creating deposit:', error);
            alert('Failed to initiate deposit. Please try again.');
        }
    };

    const verifyPaymentOnMount = useCallback(async () => {
        const urlParams = new URLSearchParams(location.search);
        const reference = urlParams.get("reference");
        // const status = urlParams.get("status");
        const savedData = localStorage.getItem("save_data");
        const payMethod = localStorage.getItem('payment_method');
        const tx_reference = urlParams.get("trxref");
        const payment_reference = localStorage.getItem("payment_reference");


        if (reference && tx_reference && savedData && payMethod == "fiat" && tx_reference == payment_reference) {
            setPaymentReference(reference);
            const cleanURL = location.pathname;
            window.history.replaceState({}, document.title, cleanURL);

            // handleVerifyPayment({ reference });

            let parsedInvestData: CreateDepositInput | null = null;

            try {
                parsedInvestData = JSON.parse(savedData) as CreateDepositInput;

                // if (!parsedInvestData) return;
                if (tx_reference != payment_reference) {
                    toast.error('Payment not completed');

                    return;
                } else {
                    console.log(parsedInvestData.payment_reference);
                    toast.success("finalizing investment...");
                    const response = await api.transaction.createDeposit({
                        amount: parsedInvestData.amount,
                        packageId: parsedInvestData.packageId,
                        payment_method: parsedInvestData.payment_method,
                        payment_reference: parsedInvestData.payment_reference
                    });
                    if (response.success) {
                        toast.success("investment created successfully 🎉");
                        setShowDepositModal(false);
                        setSelectedPackage(null);
                        setDepositAmount(0);
                        setPaymentMethod('fiat');
                        fetchData();
                        Swal.fire({
                            icon: "success",
                            title: "Congratulations 🎉",
                            text: "investment created successfully",
                        });

                    } else {
                        toast.error(response.error || 'Failed to create investment');
                        console.log(response.error)
                    }
                }

                localStorage.removeItem("payment_reference");
                localStorage.removeItem("payment_method");
                setPaymentReference(null);
                localStorage.removeItem("save_data");
            } catch (error) {
                console.error("Failed to parse save_data from localStorage:", error);

                localStorage.removeItem("payment_reference");
                localStorage.removeItem("payment_method");
                setPaymentReference(null);
                localStorage.removeItem("save_data");
            }


        }
        // else if (status == "success" && tx_reference && savedData && cryptoStatus == null && payMethod == "cypto" && paymentReference == null) {
        //     setCryptoStatus(status);
        //     setPaymentReference(reference);

        //     const cleanURL = location.pathname;
        //     window.history.replaceState({}, document.title, cleanURL);

        //     let parsedInvestData: CreateDepositInput | null = null;


        //     try {
        //         parsedInvestData = JSON.parse(savedData) as CreateDepositInput;


        //         if (!parsedInvestData) return;
        //         toast.success("finalizing investment...");
        //         const response = await api.transaction.createDeposit({
        //             amount: parsedInvestData.amount,
        //             packageId: parsedInvestData.packageId,
        //             payment_method: parsedInvestData.payment_method,
        //             payment_reference: tx_reference + "_" + reference
        //         });
        //         if (response.success) {
        //             toast.success("investment created successfully 🎉");
        //             setShowDepositModal(false);
        //             setSelectedPackage(null);
        //             setDepositAmount(0);
        //             setPaymentMethod('fiat');
        //             fetchData();
        //             Swal.fire({
        //                 icon: "success",
        //                 title: "Congratulations 🎉",
        //                 text: "investment created successfully",
        //             });

        //         } else {
        //             toast.error(response.error || 'Failed to create investment');
        //             console.log(response.error)
        //         }

        //         localStorage.removeItem("payment_reference");
        //         localStorage.removeItem("payment_method");
        //         setPaymentReference(null);
        //         localStorage.removeItem("save_data");
        //         setCryptoStatus(null);
        //     } catch (error) {
        //         console.error("Failed to parse save_data from localStorage:", error);
        //     }

        // }
    }, [location, paymentReference]);


    useEffect(() => {
        verifyPaymentOnMount();
    }, [verifyPaymentOnMount]);

    const isLoading = isAcceptingPayment;


    if (!authStoreUser) {
        navigate("/")
        return null;
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <Header variant="dashboard" />
                <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600 dark:text-gray-300">Loading savings options...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <Header variant="dashboard" />

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Hero Section */}
                <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 rounded-3xl p-8 mb-8 text-white relative overflow-hidden shadow-2xl">
                    {/* Background decorations */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-white/10 to-transparent rounded-full transform translate-x-32 -translate-y-32"></div>
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-white/5 to-transparent rounded-full transform -translate-x-24 translate-y-24"></div>

                    <div className="relative z-10">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                            <div>
                                <h1 className="text-4xl lg:text-5xl font-bold mb-4">
                                    Start Your savings Journey Today!
                                </h1>
                                <p className="text-blue-100 text-lg mb-6">
                                    Choose from our premium savings packages and start earning consistent monthly returns on your deposits.
                                </p>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
                                        <div className="flex items-center mb-2">
                                            <TrendingUp className="h-5 w-5 text-blue-200 mr-2" />
                                            <span className="text-sm text-purple-200">Current Balance</span>
                                        </div>
                                        <div className="text-2xl font-bold">${stats?.totalBalance?.toLocaleString() || '0.00'}</div>
                                    </div>
                                    <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
                                        <div className="flex items-center mb-2">
                                            <Target className="h-5 w-5 text-green-300 mr-2" />
                                            <span className="text-sm text-purple-200">Monthly Earnings</span>
                                        </div>
                                        <div className="text-2xl font-bold">${stats?.monthlyReturn?.toLocaleString() || '0.00'}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="relative">
                                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
                                    <img
                                        src={mobileBanking}
                                        alt="Secure Savings"
                                        className="w-full h-auto rounded-xl"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Why Save With Us Section */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                            <Shield className="h-8 w-8 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Secure & Protected</h3>
                        <p className="text-gray-600 dark:text-gray-300">Your deposits are secured with bank-level encryption and protection measures.</p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                        <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                            <TrendingUp className="h-8 w-8 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Guaranteed Returns</h3>
                        <p className="text-gray-600 dark:text-gray-300">Earn consistent monthly returns with our proven savings packages.</p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                        <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                            <Users className="h-8 w-8 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Referral Bonuses</h3>
                        <p className="text-gray-600 dark:text-gray-300">Earn extra bonuses when you refer friends and family to start saveing.</p>
                    </div>
                </div>

                {/* Current Package Status */}
                {stats?.current_package && stats.current_package !== 'none' && (
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-2xl p-6 mb-8 border border-green-200 dark:border-green-700">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <div className="w-12 h-12 bg-green-100 dark:bg-green-800 rounded-xl flex items-center justify-center">
                                    <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Active Savings Package</h3>
                                    <p className="text-green-600 dark:text-green-400 capitalize font-medium">
                                        {stats.activePackages
                                            .map((pkg: IUserPackage) => pkg.packageId?.name)
                                            .join(', ') || 'No active packages'}
                                    </p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-sm text-gray-600 dark:text-gray-400">Total</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                    ${stats.totalBalance?.toLocaleString() || '0.00'}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Savings Packages */}
                <div className="mb-8">
                    <div className="text-center mb-8">
                        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Choose Your Savings Package</h2>
                        <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                            Select the package that best fits your savings goals and start earning monthly returns today.
                        </p>
                    </div>

                    {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                        {packages.map((pkg, index) => (
                            <SavingsPackageCard
                                key={pkg._id}
                                package={pkg}
                                isPopular={index === 2} // Make middle package popular
                                onSelect={() => handlePackageSelect(pkg)}
                                buttonText="Get Started"
                            />
                        ))}
                    </div> */}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                        {[...packages]
                            .sort((a, b) => a.minDeposit - b.minDeposit)
                            .map((pkg, index) => (
                                <SavingsPackageCard
                                    key={pkg._id}
                                    package={pkg}
                                    isPopular={index === 2}
                                    onSelect={() => handlePackageSelect(pkg)}
                                    buttonText="Get Started"
                                />
                            ))}
                    </div>
                </div>

                {/* How Returns Work */}
                <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-lg">
                    <div className="text-center mb-8">
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">How Your Returns Work</h3>
                        <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                            Understanding how your savings grow month by month with our transparent return system.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                                <DollarSign className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                            </div>
                            <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Monthly Returns</h4>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                Earn consistent monthly returns based on your selected package rate
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Zap className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                            </div>
                            <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Automatic Processing</h4>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                Returns are automatically calculated and credited to your account
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Star className="h-8 w-8 text-green-600 dark:text-green-400" />
                            </div>
                            <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Compound Growth</h4>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                Your returns can be re-allocated to accelerate your wealth building
                            </p>
                        </div>
                    </div>
                </div>

                {/* Deposit Modal */}
                {showDepositModal && selectedPackage && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto">
                            <div className="text-center mb-6">
                                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <img src={logo} alt="logo" className="h-10" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                    Start {selectedPackage.name}
                                </h3>
                                <p className="text-gray-600 dark:text-gray-300">
                                    Begin your savings journey with {selectedPackage.monthlyReturn}% monthly returns
                                </p>
                            </div>

                            <div className="space-y-4 mb-6">
                                {/* Payment Method Selection */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                        Payment Method
                                    </label>
                                    <div className="grid grid-cols-2 gap-3 mb-4">
                                        <button
                                            type="button"
                                            onClick={() => setPaymentMethod('fiat')}
                                            className={`p-3 rounded-xl border-2 transition-all duration-200 ${paymentMethod === 'fiat'
                                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                                                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                                                }`}
                                        >
                                            <div className="text-center">
                                                <div className="font-semibold">Fiat</div>
                                                {/* <div className="text-xs opacity-75">Bank Transfer</div> */}
                                            </div>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setPaymentMethod('crypto')}
                                            className={`p-3 rounded-xl border-2 transition-all duration-200 ${paymentMethod === 'crypto'
                                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                                                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                                                }`}
                                        >
                                            <div className="text-center">
                                                <div className="font-semibold">Crypto</div>
                                                <div className="text-xs opacity-75">USDT(BSC)</div>
                                            </div>
                                        </button>
                                    </div>
                                </div>

                                {/* Deposit Amount */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Deposit Amount
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                                        <input
                                            type="number"
                                            value={depositAmount}
                                            onChange={(e) => setDepositAmount(Number(e.target.value))}
                                            min={selectedPackage.minDeposit}
                                            className="w-full pl-8 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            placeholder="Enter amount"
                                        />
                                    </div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                        Minimum: ${selectedPackage.minDeposit.toLocaleString()}
                                    </p>

                                    {/* Nigerian Naira Equivalent for Fiat */}
                                    {paymentMethod === 'fiat' && depositAmount && (
                                        <div className="mt-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-green-700 dark:text-green-400">Amount to pay in Naira:</span>
                                                <span className="font-bold text-green-800 dark:text-green-300">
                                                    ₦{(Number(depositAmount || 0) * 1500).toLocaleString('en-NG')}
                                                </span>
                                            </div>
                                            <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                                                Exchange rate: $1 = ₦1,500
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Monthly Return:</span>
                                        <span className="font-semibold text-blue-600 dark:text-blue-400">
                                            {selectedPackage.monthlyReturn}%
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Estimated Monthly Earnings:</span>
                                        <span className="font-bold text-green-600 dark:text-green-400">
                                            ${(Number(depositAmount || 0) * selectedPackage.monthlyReturn / 100).toLocaleString()}
                                        </span>
                                    </div>
                                    {paymentMethod === 'fiat' && (
                                        <div className="flex justify-between items-center border-t border-blue-200 dark:border-blue-700 pt-2 mt-2">
                                            <span className="text-sm text-gray-600 dark:text-gray-400">Monthly Earnings (NGN):</span>
                                            <span className="font-bold text-green-600 dark:text-green-400">
                                                ₦{((Number(depositAmount || 0) * selectedPackage.monthlyReturn / 100) * 1500).toLocaleString('en-NG')}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <div className="mt-4 p-4 border border-gray-200 rounded-xl bg-gray-50">
                                    <label className="flex items-start gap-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={amlAccepted}
                                            onChange={(e) => setAmlAccepted(e.target.checked)}
                                            className="mt-1 w-4 h-4 accent-blue-600"
                                        />

                                        <span className="text-sm text-gray-700 leading-relaxed">
                                            <strong>AML & Compliance Declaration:</strong><br />
                                            By proceeding, I confirm that:<br />

                                            I am not involved in money laundering<br />

                                            I am not funding terrorism<br />

                                            I am not acting on behalf of another person or entity<br />

                                            The funds used are from legitimate and lawful sources<br /><br />

                                            I confirm that all information provided is true, accurate, and complete, and I understand that false declarations may result in account suspension and reporting to relevant authorities.
                                        </span>
                                    </label>
                                </div>

                                <div className="space-y-4 w-full">
                                    {/* 1. Wallet Status Row (Only shows when Crypto is selected) */}
                                    {paymentMethod === "crypto" && (
                                        <div className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 animate-in fade-in slide-in-from-top-2">
                                            {!isConnected ? (
                                                <div className="flex flex-col items-center gap-2">
                                                    <button
                                                        onClick={() => open()}
                                                        className="flex items-center gap-2 py-2 px-6 bg-blue-600 text-white rounded-full text-sm font-bold hover:bg-blue-700 transition-all active:scale-95"
                                                    >
                                                        <Wallet size={16} />
                                                        Connect Wallet
                                                    </button>
                                                    <p className="text-[10px] text-gray-400 uppercase tracking-widest">Required for Crypto Deposit</p>
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-between w-full px-2">
                                                    <div className="flex items-center gap-2 text-sm font-mono text-gray-600 dark:text-gray-300">
                                                        <span className="text-green-500 animate-pulse">●</span>
                                                        {address?.slice(0, 6)}...{address?.slice(-4)}
                                                    </div>
                                                    <button
                                                        onClick={() => disconnect()}
                                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Disconnect"
                                                    >
                                                        <LogOut size={16} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* 2. Main Action Buttons */}
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => {
                                                setShowDepositModal(false);
                                                setSelectedPackage(null);
                                                setDepositAmount(0);
                                                setPaymentMethod('fiat');
                                            }}
                                            className="flex-1 h-12 px-6 border border-gray-300 dark:border-gray-600 rounded-xl font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                        >
                                            Cancel
                                        </button>

                                        <button
                                            onClick={handleDeposit}
                                            disabled={
                                                isLoading ||
                                                !depositAmount ||
                                                !amlAccepted ||
                                                Number(depositAmount) < selectedPackage?.minDeposit ||
                                                (paymentMethod === "crypto" && !isConnected)
                                            }
                                            className="flex-1 h-12 px-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:grayscale disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                            {isLoading ? (
                                                <span className="flex items-center gap-2">
                                                    <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                    </svg>
                                                    Processing...
                                                </span>
                                            ) : (
                                                `Pay with ${paymentMethod === 'fiat' ? 'Fiat' : 'Crypto'}`
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}

